/**
 * Building and posing a player's body.
 *
 * A player is ONE mesh — `fullbody.ase`, 747 triangles in six material groups — deformed by
 * the 14-joint skeleton, plus a separate hair mesh parented to the neck. The body-part
 * meshes that sit in the skeleton file (pelvis, trunk, head, …) are never drawn; they exist
 * so the skeleton has something to be authored against.
 *
 * The deformation runs on the CPU here, as it does in the original
 * (`HumanoidBase::UpdateFullbodyModel`, humanoidbase.cpp:437-560, called every tick from the
 * game task). At 580 vertices a player that is a rounding error, and it keeps the ported
 * shaders untouched — three.js's own skinning would need its shader chunks, and those
 * shaders are verified line by line against the original's.
 *
 *
 * THE BIND POSE, AND WHY IT TAKES TWO REFERENCE POSES
 *
 * The body mesh is not modelled in a neutral pose, so the skeleton cannot simply be inverted
 * out of it. The original solves that with two of its three reference poses
 * (`PrepareFullbodyModel`, humanoidbase.cpp:207-400):
 *
 *   "base"     the pose the mesh WAS modelled in. Rotating each vertex by the inverse of its
 *              bone's world rotation in this pose cancels the modelled pose out.
 *   "straight" supplies the joint POSITIONS the un-rotated vertices are hung from.
 *
 * The mesh is baked through both, once, and the straight-pose positions then become the
 * reference the runtime subtracts. Those positions cancel algebraically — posing the baked
 * mesh with the base pose reproduces the modelled mesh exactly — so "straight" is not a
 * second bind pose so much as a scaffold. That equivalence is asserted in the tests.
 *
 *
 * PLAYER HEIGHT
 *
 * The original scales every vertex, every reference position and every joint position by
 * `playerHeight / 1.92` before skinning (humanoidbase.cpp:102). Every one of those terms is
 * multiplied by the same factor, so the result is exactly the whole skinned body scaled
 * uniformly — which is what happens here instead: skin at full size, scale the body node.
 * Same pixels, one multiply instead of thousands. The HAIR is deliberately left outside that
 * scale, because the original scales the hair's POSITION by height but never the hair mesh
 * itself (humanoidbase.cpp:425-427).
 */

import {
  BufferGeometry,
  DynamicDrawUsage,
  Float32BufferAttribute,
  Group,
  Mesh,
  Quaternion,
  RawShaderMaterial,
  Texture,
  Vector3,
} from 'three';
import type { AssetStore } from '../assets/loader';
import { DeferredPipeline } from './deferred';
import { toDrawItems, type DrawItem } from './geometry';
import { Skeleton, type PoseData, type SkeletonData } from '../sim/skeleton';

/** humanoidbase.cpp:102 — the height every body mesh is modelled at. */
export const DEFAULT_PLAYER_HEIGHT = 1.92;

const FULLBODY_MESH = 'media/objects/players/models/fullbody.ase';
const BASE_POSE = 'media/animations/base.anim.util';
const STRAIGHT_POSE = 'media/animations/straight.anim.util';
const KIT_MATERIAL_TEXTURE = 'media/objects/players/textures/kit_template.png';
const SKIN_MATERIAL_TEXTURE = 'media/objects/players/textures/skin.jpg';

/** What makes one player look different from another. All of it comes from the database. */
export interface PlayerAppearance {
  /** Metres. Drives a uniform scale about the feet. */
  height: number;
  /** 1..4, selecting skin01.png … skin04.png (humanoidbase.cpp:109). */
  skinColor: number;
  /** A hairstyle mesh name, e.g. "short01". */
  hairStyle: string;
  /** A hair texture name, e.g. "black". */
  hairColor: string;
  /** Manifest texture key for the shirt. Goalkeepers get goalie_kit.png (team.cpp:91). */
  kitTexture: string;
}

interface SkinPrimitive {
  material: string;
  vertexCount: number;
  joints: number[];
  weights: number[];
}

interface SkinData {
  bonesPerVertex: number;
  vertexCount: number;
  primitives: SkinPrimitive[];
}

/** One player's private copy of a material group: the geometry plus the arrays behind it. */
export interface SkinTarget {
  geometry: BufferGeometry;
  position: Float32Array;
  normal: Float32Array;
  tangent: Float32Array;
  bitangent: Float32Array;
}

/** One primitive's vertices in the bind pose, ready to be skinned into a live pose. */
export interface BindPrimitive {
  item: DrawItem;
  position: Float32Array;
  normal: Float32Array;
  tangent: Float32Array;
  bitangent: Float32Array;
  joints: Int32Array;
  weights: Float32Array;
  vertexCount: number;
}

// ---------------------------------------------------------------------------

/**
 * Loads the shared player assets once, then stamps out individual players.
 *
 * The original does the same: it loads a single player template and a single body mesh, and
 * every player copies from those (`Team::InitPlayers`, team.cpp:62-99).
 */
export class HumanoidFactory {
  private constructor(
    private readonly store: AssetStore,
    private readonly skeletonData: SkeletonData,
    private readonly bindPrimitives: BindPrimitive[],
    /** Joint positions the bind pose hangs from — the "straight" pose's world positions. */
    private readonly bindJointPosition: Vector3[],
  ) {}

  static async create(store: AssetStore): Promise<HumanoidFactory> {
    const [skeletonData, basePose, straightPose, skin, gltf] = await Promise.all([
      store.skeleton<SkeletonData>(),
      store.restPose<PoseData>(BASE_POSE),
      store.restPose<PoseData>(STRAIGHT_POSE),
      store.skin<SkinData>('fullbody'),
      store.mesh(FULLBODY_MESH),
    ]);

    const items = toDrawItems(gltf);
    if (items.length !== skin.primitives.length) {
      throw new Error(
        `Body mesh has ${items.length} material groups but the skinning data describes ` +
          `${skin.primitives.length}. Re-run tools/convert/skinning.py.`,
      );
    }

    const skeleton = new Skeleton(skeletonData);

    // The pose the mesh was modelled in: keep the inverse of every bone's world rotation,
    // and the world positions those rotations turn about.
    skeleton.setPose(basePose);
    skeleton.update();
    const baseInverseRotation = skeleton.worldRotation.map((q) => q.clone().invert());
    const basePosition = skeleton.worldPosition.map((v) => v.clone());

    // The scaffold pose: where the un-rotated vertices get hung.
    skeleton.setPose(straightPose);
    skeleton.update();
    const straightPosition = skeleton.worldPosition.map((v) => v.clone());

    const bindPrimitives = items.map((item, i) =>
      bakeBindPose(item, skin.primitives[i]!, baseInverseRotation, basePosition, straightPosition),
    );

    return new HumanoidFactory(store, skeletonData, bindPrimitives, straightPosition);
  }

  /** Everything the body mesh needs, resolved per player. */
  async build(appearance: PlayerAppearance): Promise<Humanoid> {
    const skinTexture = `media/objects/players/textures/skin0${appearance.skinColor}.png`;

    const meshes: Mesh[] = [];
    const targets: SkinTarget[] = [];
    /** Kept so the shirt can be changed later without rebuilding the player. */
    let kitMesh: Mesh | null = null;
    for (const bind of this.bindPrimitives) {
      const info = bind.item.material;

      // Two of the six material groups are re-textured per player: the shirt and the skin.
      // Both also override the material's shine, which is why this is not just a texture
      // swap (SetKit, humanoidbase.cpp:1057-1081; skin, humanoidbase.cpp:117-127).
      let albedoKey = info.albedo;
      let shininess = info.shininess;
      let specularAmount = info.specularAmount;
      if (info.albedo === KIT_MATERIAL_TEXTURE) {
        albedoKey = appearance.kitTexture;
        shininess = 0.01;
        specularAmount = 0.01;
      } else if (info.albedo === SKIN_MATERIAL_TEXTURE) {
        albedoKey = skinTexture;
        shininess = 0.2;
        specularAmount = 0.002;
      }

      const textures = await this.resolveTextures(info, albedoKey);
      const material = DeferredPipeline.createGeometryMaterial(
        { ...bind.item, material: { ...info, shininess, specularAmount } },
        textures,
      );

      const target = cloneSkinnableGeometry(bind);
      const mesh = new Mesh(target.geometry, material);
      // The body is deformed every tick, so its bounds are never what three computed at
      // load. Culling it against those would make players vanish at the screen edge.
      mesh.frustumCulled = false;
      if (info.albedo === KIT_MATERIAL_TEXTURE) kitMesh = mesh;
      meshes.push(mesh);
      targets.push(target);
    }

    // Said out loud rather than left to be discovered: without it the shirt would still be
    // drawn, and only changing kit mid-match would quietly do nothing.
    if (!kitMesh) console.warn('[gf] the body mesh has no shirt material group');

    const hair = await this.buildHair(appearance);

    return new Humanoid(
      new Skeleton(this.skeletonData),
      this.bindPrimitives,
      this.bindJointPosition,
      meshes,
      targets,
      hair,
      appearance.height / DEFAULT_PLAYER_HEIGHT,
      kitMesh,
    );
  }

  private async resolveTextures(
    info: DrawItem['material'],
    albedoKey: string | undefined,
  ): Promise<Parameters<typeof DeferredPipeline.createGeometryMaterial>[1]> {
    return {
      albedo: albedoKey ? await this.store.texture(albedoKey, true) : null,
      normal: info.normal ? await this.store.texture(info.normal, false) : null,
      specular: info.specular ? await this.store.texture(info.specular, false) : null,
      illumination: info.illumination ? await this.store.texture(info.illumination, false) : null,
    };
  }

  private async buildHair(appearance: PlayerAppearance): Promise<Mesh | null> {
    const key = `media/objects/players/hairstyles/${appearance.hairStyle}.ase`;
    try {
      const gltf = await this.store.mesh(key);
      const items = toDrawItems(gltf);
      const item = items[0];
      if (!item) return null;

      // Every hairstyle is drawn with the player's hair colour and the same dull shine,
      // whatever the mesh's own material said (humanoidbase.cpp:158-167).
      const texture: Texture = await this.store.texture(
        `media/objects/players/textures/hair/${appearance.hairColor}.png`,
        true,
      );
      const material = DeferredPipeline.createGeometryMaterial(
        { ...item, material: { ...item.material, shininess: 0.05, specularAmount: 0.01 } },
        { albedo: texture, normal: null, specular: null, illumination: null },
      );
      const mesh = new Mesh(item.geometry, material);
      mesh.frustumCulled = false;
      return mesh;
    } catch (error) {
      console.warn(`[gf] hairstyle "${appearance.hairStyle}" failed to load`, error);
      return null;
    }
  }
}

// ---------------------------------------------------------------------------

/**
 * One player's body, in the SIMULATION frame (Z-up, metres, feet at z = 0).
 *
 * Put `object` under a node that carries the sim-to-render rotation; nothing in here knows
 * about the render frame.
 */
export class Humanoid {
  readonly object = new Group();
  readonly skeleton: Skeleton;

  private readonly body = new Group();
  private readonly meshes: Mesh[];
  private readonly targets: SkinTarget[];
  private readonly hair: Mesh | null;
  private readonly bindPrimitives: BindPrimitive[];
  private readonly bindJointPosition: Vector3[];
  private readonly neckJoint: number;
  private readonly heightScale: number;
  private readonly kitMesh: Mesh | null;

  constructor(
    skeleton: Skeleton,
    bindPrimitives: BindPrimitive[],
    bindJointPosition: Vector3[],
    meshes: Mesh[],
    targets: SkinTarget[],
    hair: Mesh | null,
    heightScale: number,
    kitMesh: Mesh | null = null,
  ) {
    this.skeleton = skeleton;
    this.bindPrimitives = bindPrimitives;
    this.bindJointPosition = bindJointPosition;
    this.meshes = meshes;
    this.targets = targets;
    this.hair = hair;
    this.kitMesh = kitMesh;
    this.neckJoint = skeleton.indexOf('neck');

    this.heightScale = heightScale;
    this.body.scale.setScalar(heightScale);
    for (const mesh of meshes) this.body.add(mesh);
    this.object.add(this.body);
    if (hair) this.object.add(hair);
  }

  /** Draw calls this player costs: one per material group, plus a hairstyle if he has one. */
  get drawCount(): number {
    return this.meshes.length + (this.hair ? 1 : 0);
  }

  /** Where the player stands, in metres. */
  setPosition(position: Vector3): void {
    this.object.position.copy(position);
  }

  /** Radians about the up axis, measured from the original's rest facing of (0, -1, 0). */
  setFacing(radians: number): void {
    this.skeleton.setFacing(radians);
  }

  /**
   * Put this player in a different shirt. Only the image changes: the shirt's shine was set
   * when he was built and is the same for every kit (`SetKit`, humanoidbase.cpp:1057-1081).
   */
  setKitTexture(texture: Texture | null): void {
    const material = this.kitMesh?.material as RawShaderMaterial | undefined;
    const uniform = material?.uniforms['map_albedo'];
    if (uniform) uniform.value = texture;
  }

  /**
   * Re-pose and re-deform. Call after changing the skeleton's pose or facing.
   *
   * This is `UpdateFullbodyNodes` followed by `UpdateFullbodyModel`
   * (humanoidbase.cpp:403-560), minus the original's low-detail mode, which skins distant
   * players on alternate frames only.
   */
  update(): void {
    this.skeleton.update();

    const rotation = this.skeleton.worldRotation;
    const position = this.skeleton.worldPosition;

    for (let p = 0; p < this.bindPrimitives.length; p++) {
      skinPrimitive(
        this.bindPrimitives[p]!,
        this.targets[p]!,
        rotation,
        position,
        this.bindJointPosition,
      );
    }

    if (this.hair) {
      this.hair.quaternion.copy(rotation[this.neckJoint]!);
      this.hair.position
        .copy(position[this.neckJoint]!)
        .multiplyScalar(this.heightScale);
    }
  }

  dispose(): void {
    for (const mesh of this.meshes) {
      mesh.geometry.dispose();
      (mesh.material as { dispose(): void }).dispose();
    }
    this.hair?.geometry.dispose();
  }
}

// ---------------------------------------------------------------------------
// The maths
// ---------------------------------------------------------------------------

/** Positions plus the three direction vectors, three floats each, one entry per vertex. */
export interface VertexArrays {
  position: Float32Array;
  normal: Float32Array;
  tangent: Float32Array;
  bitangent: Float32Array;
}

export interface BoneWeights {
  /** Three joint indices per vertex. Unused slots hold 0 and carry weight 0. */
  joints: ArrayLike<number>;
  /** Three weights per vertex, matching `joints`. */
  weights: ArrayLike<number>;
}

/**
 * Move vertices from one set of bone transforms to another.
 *
 * For each vertex, and each bone that claims part of it: take the vertex relative to where
 * that bone WAS, turn it by `rotation`, and put it where that bone IS. Weighted-average the
 * results; direction vectors are only turned, then renormalised.
 *
 *     out = SUM over bones of  weight * ( rotation_b * (v - from_b) + to_b )
 *
 * Both halves of the skinning pipeline are this one operation, which is why it is one
 * function. The bind bake runs it with the inverse of the modelled pose's rotations, from
 * the modelled pose's joint positions to the scaffold pose's; posing runs it with the live
 * rotations, from the scaffold positions to the live ones. Doing the first and then the
 * second with the modelled pose returns the vertices exactly as they were authored — the
 * scaffold positions cancel — and the tests check that.
 *
 * `out` may not alias `source`.
 */
export function deformVertices(
  source: VertexArrays,
  bones: BoneWeights,
  vertexCount: number,
  rotation: Quaternion[],
  from: Vector3[],
  to: Vector3[],
  out: VertexArrays,
): void {
  const v = new Vector3();
  const acc = new Vector3();
  const accNormal = new Vector3();
  const accTangent = new Vector3();
  const accBitangent = new Vector3();
  const temp = new Vector3();

  for (let i = 0; i < vertexCount; i++) {
    acc.set(0, 0, 0);
    accNormal.set(0, 0, 0);
    accTangent.set(0, 0, 0);
    accBitangent.set(0, 0, 0);

    for (let b = 0; b < 3; b++) {
      const weight = bones.weights[i * 3 + b]!;
      if (weight === 0) continue;
      const joint = bones.joints[i * 3 + b]!;
      const turn = rotation[joint]!;

      v.fromArray(source.position, i * 3)
        .sub(from[joint]!)
        .applyQuaternion(turn)
        .add(to[joint]!);
      acc.addScaledVector(v, weight);

      temp.fromArray(source.normal, i * 3).applyQuaternion(turn);
      accNormal.addScaledVector(temp, weight);
      temp.fromArray(source.tangent, i * 3).applyQuaternion(turn);
      accTangent.addScaledVector(temp, weight);
      temp.fromArray(source.bitangent, i * 3).applyQuaternion(turn);
      accBitangent.addScaledVector(temp, weight);
    }

    acc.toArray(out.position, i * 3);
    accNormal.normalize().toArray(out.normal, i * 3);
    accTangent.normalize().toArray(out.tangent, i * 3);
    accBitangent.normalize().toArray(out.bitangent, i * 3);
  }
}

/** Cancel the modelled pose out of one material group's vertices. */
function bakeBindPose(
  item: DrawItem,
  skin: SkinPrimitive,
  baseInverseRotation: Quaternion[],
  basePosition: Vector3[],
  straightPosition: Vector3[],
): BindPrimitive {
  const source = readVertexArrays(item);
  const count = source.count;

  if (count !== skin.vertexCount) {
    throw new Error(
      `Material group "${skin.material}" has ${count} vertices but the skinning data ` +
        `describes ${skin.vertexCount}. Re-run tools/convert/skinning.py.`,
    );
  }

  const out: VertexArrays = {
    position: new Float32Array(count * 3),
    normal: new Float32Array(count * 3),
    tangent: new Float32Array(count * 3),
    bitangent: new Float32Array(count * 3),
  };

  deformVertices(source, skin, count, baseInverseRotation, basePosition, straightPosition, out);

  return {
    item,
    ...out,
    joints: Int32Array.from(skin.joints),
    weights: Float32Array.from(skin.weights),
    vertexCount: count,
  };
}

/** Deform one material group into the skeleton's current pose. */
function skinPrimitive(
  bind: BindPrimitive,
  target: SkinTarget,
  worldRotation: Quaternion[],
  worldPosition: Vector3[],
  bindJointPosition: Vector3[],
): void {
  deformVertices(
    bind,
    bind,
    bind.vertexCount,
    worldRotation,
    bindJointPosition,
    worldPosition,
    target,
  );

  target.geometry.getAttribute('position').needsUpdate = true;
  target.geometry.getAttribute('normal').needsUpdate = true;
  target.geometry.getAttribute('tangent').needsUpdate = true;
  target.geometry.getAttribute('bitangent').needsUpdate = true;
}

// ---------------------------------------------------------------------------

/**
 * Copy one material group's vertex data out into plain, tightly packed arrays.
 *
 * A TRAP WORTH KNOWING ABOUT: the converted glTF is interleaved, so three.js hands back
 * InterleavedBufferAttributes whose `.array` is the WHOLE vertex block — position, normal,
 * tangent and texture coordinates side by side, twelve floats per vertex. Copying `.array`
 * and then indexing it three floats at a time reads normals and tangents as if they were
 * positions. It does not throw and it does not look empty; the body just folds into a heap.
 * Always go through getX/getY/getZ, which know the stride.
 */
function readVertexArrays(item: DrawItem): {
  count: number;
  position: Float32Array;
  normal: Float32Array;
  tangent: Float32Array;
  bitangent: Float32Array;
} {
  const geometry = item.geometry;
  const position = geometry.getAttribute('position');
  const normal = geometry.getAttribute('normal');
  const tangent = geometry.getAttribute('tangent');
  const bitangent = geometry.getAttribute('bitangent');

  if (!position || !normal || !tangent || !bitangent) {
    throw new Error(`Body mesh group "${item.name}" is missing a vertex attribute`);
  }

  const count = position.count;

  // glTF stores the tangent as vec4 with handedness in w; the shader wants vec3 and takes
  // the handedness through the separate bitangent, which geometry.ts has already computed.
  const unpack = (source: typeof position): Float32Array => {
    const out = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      out[i * 3] = source.getX(i);
      out[i * 3 + 1] = source.getY(i);
      out[i * 3 + 2] = source.getZ(i);
    }
    return out;
  };

  return {
    count,
    position: unpack(position),
    normal: unpack(normal),
    tangent: unpack(tangent),
    bitangent: unpack(bitangent),
  };
}

/**
 * A per-player copy of one material group.
 *
 * Positions and the three direction vectors have to be private to each player, because each
 * is in a different pose. Texture coordinates and the index buffer are identical for
 * everybody and are shared.
 */
export function cloneSkinnableGeometry(bind: BindPrimitive): SkinTarget {
  const source = bind.item.geometry;
  const geometry = new BufferGeometry();

  /**
   * Attach one attribute and hand back THE ARRAY THE GEOMETRY IS ACTUALLY USING.
   *
   * A TRAP THAT COSTS A DAY: `Float32BufferAttribute` COPIES the array you give it
   * (`super(new Float32Array(array), ...)`). Keep a reference to the array you passed in
   * and write into that, and every write lands in a detached copy the renderer never
   * reads. Nothing errors, nothing looks empty — the body simply stays frozen in whatever
   * pose it was built in, which reads as "the animation is not playing" and sends you
   * hunting through the animation code instead. Always take the array back off the
   * attribute afterwards.
   *
   * The usage hint is a small win on top: these four arrays are rewritten every frame.
   */
  const attach = (name: string, values: Float32Array): Float32Array => {
    const attribute = new Float32BufferAttribute(values, 3);
    attribute.setUsage(DynamicDrawUsage);
    geometry.setAttribute(name, attribute);
    return attribute.array as Float32Array;
  };

  // Own, tightly packed arrays — never a view into a shared interleaved block, so the
  // skinning loop can write straight into them.
  const position = attach('position', bind.position);
  const normal = attach('normal', bind.normal);
  const tangent = attach('tangent', bind.tangent);
  const bitangent = attach('bitangent', bind.bitangent);

  const texcoord = source.getAttribute('texcoord');
  if (texcoord) geometry.setAttribute('texcoord', texcoord);
  const index = source.getIndex();
  if (index) geometry.setIndex(index);

  return { geometry, position, normal, tangent, bitangent };
}
